import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  IconButton,
  useTheme,
  Slide
} from '@mui/material';
import { 
  Close as CloseIcon,
  GetApp as GetAppIcon,
  Phone as PhoneIcon,
  Computer as ComputerIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import useInstallPrompt from '../hooks/useInstallPrompt';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const InstallPromptModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const {
    browserType,
    promptInstall,
    markAsDismissed,
    markAsDismissedPermanent,
    getInstallInstructions,
    deferredPrompt
  } = useInstallPrompt();

  const instructions = getInstallInstructions;

  const handleInstall = async () => {
    if (deferredPrompt) {
      // For browsers with native install prompt
      await promptInstall();
      onClose();
    } else {
      // For Safari/iOS - just close modal as instructions are shown
      markAsDismissed();
      onClose();
    }
  };

  const handleDismiss = () => {
    markAsDismissed();
    onClose();
  };

  const handleNeverShow = () => {
    markAsDismissedPermanent();
    onClose();
  };

  const getBrowserIcon = () => {
    switch (browserType) {
      case 'ios':
        return <PhoneIcon sx={{ fontSize: 48, color: colors.primary[400] }} />;
      case 'safari':
        return <ComputerIcon sx={{ fontSize: 48, color: colors.primary[400] }} />;
      case 'chrome':
      case 'edge':
      case 'samsung':
        return <GetAppIcon sx={{ fontSize: 48, color: colors.primary[400] }} />;
      default:
        return <GetAppIcon sx={{ fontSize: 48, color: colors.primary[400] }} />;
    }
  };

  const getStepIcon = (index) => {
    const stepColors = [colors.primary[400], colors.blue[400], colors.green[400]];
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: stepColors[index] || colors.grey[400],
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 'bold'
        }}
      >
        {index + 1}
      </Box>
    );
  };

  if (!instructions) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {getBrowserIcon()}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {instructions.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('install_app_subtitle')}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box mb={3}>
          <Typography variant="body1" color="text.secondary" mb={2}>
            {browserType === 'ios' || browserType === 'safari' 
              ? t('install_manual_instructions')
              : t('install_automatic_instructions')
            }
          </Typography>
        </Box>

        {/* Benefits Section */}
        <Box 
          sx={{ 
            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
            borderRadius: 3,
            p: 2,
            mb: 3
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            {t('why_install')}
          </Typography>
          <List dense>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Box sx={{ fontSize: 16 }}>⚡</Box>
              </ListItemIcon>
              <ListItemText 
                primary={t('install_benefit_faster')}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Box sx={{ fontSize: 16 }}>📱</Box>
              </ListItemIcon>
              <ListItemText 
                primary={t('install_benefit_homescreen')}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Box sx={{ fontSize: 16 }}>🔔</Box>
              </ListItemIcon>
              <ListItemText 
                primary={t('install_benefit_notifications')}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </Box>

        {/* Installation Steps */}
        <Typography variant="subtitle2" fontWeight="bold" mb={2}>
          {t('installation_steps')}
        </Typography>
        <List>
          {instructions.steps.map((step, index) => (
            <ListItem key={index} sx={{ py: 1 }}>
              <ListItemIcon>
                {getStepIcon(index)}
              </ListItemIcon>
              <ListItemText 
                primary={step}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>

        {/* Safari/iOS specific help */}
        {(browserType === 'ios' || browserType === 'safari') && (
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 2,
              p: 2,
              borderRadius: 2,
              background: colors.blue[100],
              color: colors.blue[800]
            }}
          >
            <ShareIcon />
            <Typography variant="body2">
              {browserType === 'ios' 
                ? t('ios_share_button_help')
                : t('safari_share_button_help')
              }
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleNeverShow}
          color="inherit"
          variant="text"
        >
          {t('never_show_again')}
        </Button>

        <Button
          onClick={handleDismiss}
          color="inherit"
          variant="outlined"
        >
          {t('not_now')}
        </Button>

        {deferredPrompt ? (
          <Button
            onClick={handleInstall}
            color="primary"
            variant="contained"
            startIcon={<GetAppIcon />}
          >
            {t('install_now')}
          </Button>
        ) : (
          <Button
            onClick={handleInstall}
            color="primary"
            variant="contained"
          >
            {t('got_it')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InstallPromptModal; 